import type { GenericField, GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Label } from '@neo/primitives/label'
import InlineEdit from '@neo/shared/components/inline-edit'
import type { DeepKeys } from '@tanstack/react-form'
import { PlusIcon, X } from 'lucide-react'
import { v4 } from 'uuid'
import { TemplateSelect } from 'v2/pages/conversations/outbound/components/template-select'
import {
  VariableEditor,
  type WhatsAppFormType,
} from 'v2/pages/conversations/outbound/components/variable-editor'
import { useWhatsAppTemplatesQuery } from 'v2/pages/settings/whatsapp/templates/queries'
import type { FileMimeType, RFileUploadResponse } from 'v2/types/api/auth'
import z from 'zod'
import { MediaUpload } from '../components/media-upload/media-upload'
import { NodevarList } from '../components/nodevar-list'
import { v2validations } from '../config/validation.config'
import {
  type AiAgentWhatsappNodeData,
  type AiAgentWhatsappNodeformData,
  FlowNodeTypes,
  type SidebarComponentProps,
} from '../types'
import { aiAgentsFields } from './ai-agents/ai-agents-fields'
import { GenericSidebarComponent } from './sidebar.base'

const whatsappSpecificFields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericField<AiAgentWhatsappNodeformData>[] => {
  // Get the validation schema
  const validationSchema = v2validations({ nodeVars }).ai_agent_whatsapp

  return [
    {
      name: 'from',
      label: 'Send WhatsApp from',
      zodValidation: (values) => {
        if (values.conversation_type === 'preset_message') {
          return z.string().min(1, 'From is required')
        }
        return z.string().optional()
      },
      componentType: 'input',
      visible: (values) => values.conversation_type === 'preset_message',
      onChangeListenTo: ['conversation_type'],
      inputProps: {
        placeholder: 'Enter',
        endIcon: (
          <NodevarList
            nodevars={fieldsWithDynamicNodeVars.from}
            className="pr-2"
            inputElementSelector="[name=from]"
            replaceEntireField
          />
        ),
      },
      afterOnChange: (_, __, form) => {
        // Reset template and variables when from changes
        form?.setFieldValue('template', {})
        form?.setFieldValue('variables', {
          header: [],
          body: [],
        })
      },
    },
    {
      name: 'to',
      label: 'Send WhatsApp to',
      zodValidation: (values) => {
        if (values.conversation_type === 'preset_message') {
          return z.string().min(1, 'To is required')
        }
        return z.string().optional()
      },
      componentType: 'input',
      visible: (values) => values.conversation_type === 'preset_message',
      onChangeListenTo: ['conversation_type'],
      inputProps: {
        placeholder: 'Enter',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=to]" />
        ),
      },
    },
    {
      name: 'whatsapp_msg_type',
      label: 'WhatsApp message type',
      zodValidation: validationSchema.shape.whatsapp_msg_type,
      componentType: 'select',
      visible: (values) => values.conversation_type === 'preset_message',
      onChangeListenTo: ['conversation_type'],
      afterOnChange: (_, __, form) => {
        // Reset fields based on message type
        form?.setFieldValue('template', {})
        form?.setFieldValue('variables', {
          header: [],
          body: [],
        })
        form?.setFieldValue('whatsapp_media', [])
        form?.setFieldValue('text', '')
        form?.setFieldValue('header_type', 'none')
        form?.setFieldValue('header_text', '')
        form?.setFieldValue('header_media', undefined)
        form?.setFieldValue('body', '')
        form?.setFieldValue('footer', '')

        // Initialize with one whatsapp_button if the type is 'whatsapp_button'
        const newMsgType = form?.state.values.whatsapp_msg_type
        if (newMsgType === 'whatsapp_button') {
          form?.setFieldValue('buttons', [
            {
              title: 'Button 1',
              id: Math.random().toString(36).substr(2, 9),
            },
          ])
          // Since we are setting buttons, we need to enable wait_for_response and set a reply_timeout
          form?.setFieldValue('wait_for_response', true)
          form?.setFieldValue('reply_timeout', 3600)
        } else {
          form?.setFieldValue('buttons', [])
        }
        if (newMsgType === 'whatsapp_list') {
          // Initialize with one section and one row for whatsapp_list
          form?.setFieldValue('whatsapp_list', {
            button: 'Options',
          })
          form?.validateField('whatsapp_list.button', 'change')
          form?.setFieldValue('sections', [
            {
              title: 'Section title 1',
              rows: [
                {
                  title: 'Row title 1',
                  description: '',
                  id: v4(),
                },
              ],
            },
          ])
          // Also set wait_for_response and reply_timeout for list messages
          form?.setFieldValue('wait_for_response', true)
          form?.setFieldValue('reply_timeout', 3600)
        } else {
          form?.setFieldValue('sections', [])
        }
      },
      inputProps: {
        options: [
          { label: 'Text', value: 'text' },
          { label: 'Image/Audio/Video/Document', value: 'whatsapp_media' },
          { label: 'WhatsApp template', value: 'template' },
          { label: 'Message with buttons', value: 'whatsapp_button' },
          { label: 'Message with list', value: 'whatsapp_list' },
        ],
      },
    },
    {
      name: 'whatsapp_media',
      type: 'custom',
      label: 'Upload media',
      zodValidation: (values) => {
        if (values.whatsapp_msg_type === 'whatsapp_media') {
          return validationSchema.shape.whatsapp_media.refine((files) => files.length > 0, {
            message: 'At least one media file must be attached',
          })
        }
        return validationSchema.shape.whatsapp_media
      },
      onChangeListenTo: ['whatsapp_msg_type', 'text', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_media',
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<AiAgentWhatsappNodeformData>
        onChange: (value: unknown[]) => void
      }) => {
        const onURLChange = (uploadedFile: RFileUploadResponse) => {
          const mediaFiles = form.state.values.whatsapp_media || []
          const newMediaFile = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange([...mediaFiles, newMediaFile])
        }

        const onRemove = (fileToRemove: RFileUploadResponse) => {
          const mediaFiles = form.state.values.whatsapp_media || []
          const updatedMediaFiles = mediaFiles.filter(
            (file) => file.file_id !== fileToRemove.file_id,
          )
          onChange(updatedMediaFiles)
        }

        // Convert whatsapp_media to RFileUploadResponse format for MediaUpload
        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.whatsapp_media) {
          initialFiles = form.state.values.whatsapp_media.map((file) => ({
            file_type: file.media_type as FileMimeType,
            file_name: file.name || '',
            file_url: file.url || '',
            file_size: file.size || 0,
            file_id: file.file_id || '',
          }))
        }

        return (
          <MediaUpload
            onURLChange={onURLChange}
            onRemove={onRemove}
            label="Upload media"
            initialFiles={initialFiles}
            nodeVars={nodeVars}
            multiUpload
            disableFiles={
              (form.state.values.text?.length ?? 0) > 0 ? ['audio', 'application'] : ['application']
            }
          />
        )
      },
    },
    {
      name: 'text',
      label: 'Text',
      zodValidation: (values) => {
        if (values.whatsapp_msg_type !== 'text') return z.string().optional()

        const hasAudioOrDoc = values.whatsapp_media?.some(
          (whatsapp_media) =>
            whatsapp_media.media_type?.includes('audio') ||
            whatsapp_media.media_type?.includes('application'),
        )

        if (hasAudioOrDoc)
          return z.string().max(0, 'Text must be empty when audio or document is uploaded')

        return z.string().min(1, 'Text is required when whatsapp message type is text')
      },
      componentType: 'textarea',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        ['text', 'whatsapp_media'].includes(values.whatsapp_msg_type),
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=text]" />
        ),
      },
    },
    {
      name: 'header_type',
      label: 'Header type',
      zodValidation: z.enum(['text', 'media', 'none']).optional(),
      componentType: 'select',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_button',
      inputProps: {
        options: [
          { label: 'Text', value: 'text' },
          { label: 'Image/Video/Document', value: 'media' },
          { label: 'None', value: 'none' },
        ],
      },
    },
    {
      name: 'header_media',
      type: 'custom',
      label: 'Header media',
      zodValidation: (values) => {
        if (values.header_type === 'media') {
          return validationSchema.shape.header_media.refine(
            (media) => media && Object.keys(media).length > 0,
            {
              message: 'Header media is required when header type is media',
            },
          )
        }
        return validationSchema.shape.header_media
      },
      onChangeListenTo: ['whatsapp_msg_type', 'header_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_button' &&
        values.header_type === 'media',
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<AiAgentWhatsappNodeformData>
        onChange: (value: unknown) => void
      }) => {
        const onURLChange = (uploadedFile: RFileUploadResponse) => {
          const newMediaFile = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange(newMediaFile)
        }

        const onRemove = () => {
          onChange(null)
        }

        // Convert header_media to RFileUploadResponse format for MediaUpload
        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.header_media) {
          initialFiles = [
            {
              file_type: form.state.values.header_media.media_type as FileMimeType,
              file_name: form.state.values.header_media.name || '',
              file_url: form.state.values.header_media.url || '',
              file_size: form.state.values.header_media.size || 0,
              file_id: form.state.values.header_media.file_id || '',
            },
          ]
        }

        return (
          <MediaUpload
            onURLChange={onURLChange}
            onRemove={onRemove}
            label="Upload header media"
            initialFiles={initialFiles}
            nodeVars={nodeVars}
            multiUpload={false}
            disableFiles={['audio', 'application']}
          />
        )
      },
    },
    {
      name: 'header_text',
      label: 'Header text',
      zodValidation: (values) => {
        if (values.header_type === 'text')
          return z
            .string()
            .min(1, 'Header text is required')
            .max(60, 'Header cannot exceed 60 characters')
        return z.string().optional()
      },
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'header_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_button' &&
        values.header_type === 'text',
      inputProps: {
        placeholder: 'Enter header text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=header_text]"
          />
        ),
      },
    },
    {
      name: 'whatsapp_list.header',
      label: 'Header (optional)',
      zodValidation: validationSchema.shape.whatsapp_list.unwrap().shape.header,
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'header_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter header text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.header']"
          />
        ),
      },
    },
    {
      name: 'body',
      label: 'Body',
      zodValidation: (values) => {
        if (
          values.whatsapp_msg_type === 'whatsapp_button' &&
          values.conversation_type === 'preset_message'
        )
          return z
            .string()
            .min(1, 'Body is required')
            .max(1024, 'Body cannot exceed 1024 characters')
        return z.string().optional()
      },
      componentType: 'textarea',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_button',
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=body]" />
        ),
      },
    },
    {
      name: 'whatsapp_list.body',
      label: 'Body',
      zodValidation: (values) => {
        if (values.whatsapp_msg_type === 'whatsapp_list')
          return validationSchema.shape.whatsapp_list
            .unwrap()
            .shape.body.refine((body) => (body?.length || 0) > 0, {
              message: 'Body is required',
            })
        return validationSchema.shape.whatsapp_list.unwrap().shape.body
      },
      componentType: 'textarea',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter text here',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.body']"
          />
        ),
      },
    },
    {
      name: 'footer',
      label: 'Footer',
      zodValidation: z.string().max(60, 'Footer cannot exceed 60 characters').optional(),
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_button',
      inputProps: {
        placeholder: 'Enter footer text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='footer']"
          />
        ),
      },
    },
    {
      name: 'whatsapp_list.footer',
      label: 'Footer',
      zodValidation: validationSchema.shape.whatsapp_list.unwrap().shape.footer,
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter footer text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.footer']"
          />
        ),
      },
    },
    {
      name: 'whatsapp_list.button',
      label: 'Button',
      zodValidation: (values) => {
        if (values.whatsapp_msg_type === 'whatsapp_list')
          return validationSchema.shape.whatsapp_list
            .unwrap()
            .shape.button.refine((button) => (button?.length || 0) > 0, {
              message: 'Button is required',
            })
        return validationSchema.shape.whatsapp_list.unwrap().shape.button
      },
      componentType: 'input',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_list',
      inputProps: {
        placeholder: 'Enter Button text',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name='whatsapp_list.button']"
          />
        ),
      },
    },
    {
      name: 'buttons',
      type: 'array',
      label: (
        <Label className="col-span-full text-s-500 mb-2">
          Configure buttons <i className="text-xsi-400">(max 3 supported)</i>
        </Label>
      ),
      // TODO: @samuellawerentz Tech Debt - Add parent container styling with hack. We should add the ability to add parent container styling to the form
      className: 'flex gap-4 p-4 rounded-md bg-bg2 w-full relative -mt-4 col-span-full',
      // TODO: @samuellawerentz Tech Debt - Fix onChangeListenTo to for array type form fields
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_button',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      defaultNewValue: (form) => ({
        title: `Button ${(form.state.values.buttons?.length || 0) + 1}`,
        id: v4(),
      }),
      zodValidation: (values) => {
        if (values.whatsapp_msg_type !== 'whatsapp_button') return z.any()
        return z
          .array(
            z.object({
              id: z.string(),
              title: z.string().min(1, 'Button text is required').max(25, 'Max 25 characters'),
            }),
          )
          .min(1, 'At least one whatsapp_button is required')
          .max(3, 'Maximum 3 buttons allowed')
      },
      addItemButton: ({ state, ...rest }) => (
        <div className="col-span-full flex justify-start bg-bg2 -mt-4 rounded-b-md items-start pb-2">
          <Button
            size="sm"
            variant="ghost"
            type="button"
            className="ml-2 form-button"
            {...rest}
            disabled={(state?.buttons?.length || 0) >= 3}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Button
          </Button>
        </div>
      ),
      removeItemButton: <X className="w-4 h-4 absolute top-3 right-8 text-tertiary" />,
      subFields: [
        {
          name: 'buttons[0].title',
          label: 'Button Text',
          componentType: 'input',
          className: 'w-full',
          zodValidation: z.string().min(1, 'Button text is required').max(25, 'Max 25 characters'),
          inputProps: {
            placeholder: 'Enter whatsapp_button text',
          },
        },
      ],
    },
    {
      name: 'sections',
      type: 'array',
      label: (
        <Label className="col-span-full text-s-500 mt-2 -mb-2">
          Configure list <i className="text-xsi-400">(max 10 supported)</i>
        </Label>
      ),
      className:
        'flex flex-col gap-2 p-4 rounded-lg bg-bg2 w-full relative mt-2 col-span-full border border-st2',
      visible: (values) =>
        values.conversation_type === 'preset_message' &&
        values.whatsapp_msg_type === 'whatsapp_list',
      onChangeListenTo: ['whatsapp_msg_type', 'conversation_type'],
      defaultNewValue: (form) => ({
        title: `Section title ${(form.state.values.sections?.length || 0) + 1}`,
        rows: [
          {
            title: 'Row title 1',
            description: '',
            id: v4(),
          },
        ],
      }),
      zodValidation: (values) => {
        if (values.whatsapp_msg_type !== 'whatsapp_list') return z.any()

        // Define the base schema for sections with min/max constraints
        const baseSectionsSchema = z
          .array(
            z.object({
              title: z.string().max(24, 'Section title cannot exceed 24 characters'),
              rows: z
                .array(
                  z.object({
                    id: z.string(),
                    title: z.string().min(1, 'Row title is required').max(24, 'Max 24 characters'),
                    description: z.string().max(72, 'Max 72 characters').optional(),
                  }),
                )
                .min(1, 'At least one row is required')
                .max(10, 'Maximum 10 rows allowed per section'),
            }),
          )
          .min(1, 'At least one section is required')
          .max(10, 'Maximum 10 sections allowed')

        // Apply refinement to check total rows
        return baseSectionsSchema.superRefine((sections, ctx) => {
          const totalRows = (sections ?? []).reduce(
            (
              sum: number,
              section: { rows?: { title: string; id: string; description?: string }[] },
            ) => sum + (section?.rows?.length || 0),
            0,
          )
          if (totalRows > 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Total number of rows across all sections cannot exceed 10.',
              path: [],
            })
          }
        })
      },
      addItemButton: ({ state, ...rest }) => (
        <div className="col-span-full flex justify-start items-start pb-2">
          <Button
            size="sm"
            variant="ghost"
            type="button"
            className="ml-0 pl-0 form-button"
            {...rest}
            disabled={(state?.sections?.length || 0) >= 10}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add section
          </Button>
        </div>
      ),
      removeItemButton: <X className="w-4 h-4 absolute top-4 right-3 text-tertiary" />,
      subFields: [
        {
          name: 'sections[0].title',
          type: 'custom',
          className: 'w-full',
          zodValidation: z.string().max(24, 'Section title cannot exceed 24 characters'),
          component: ({ form, baseName }) => {
            const fieldName = `${baseName}.title` as DeepKeys<AiAgentWhatsappNodeformData>
            const initialValue = form.getFieldValue(fieldName) || ''

            return (
              <InlineEdit
                name={fieldName}
                initialHtml={initialValue}
                onChange={(_, value) => {
                  form.setFieldValue(fieldName, value)
                }}
                onBlur={(_, value) => {
                  form.setFieldValue(fieldName, value)
                  form.validateField(fieldName, 'blur')
                }}
                tagName="span"
                className="border border-transparent hover:border-b-2 hover:border-b-st2 focus:border-b-2 focus:border-b-st2 p-1 pl-0 rounded flex-grow min-h-[36px] text-s-600"
                focusOnMount={false}
              />
            )
          },
        },
        {
          name: 'sections[0].rows',
          type: 'array',
          className: 'flex flex-col gap-3 w-full mt-2 relative',
          defaultNewValue: (_, field) => {
            const nextRowIndex = Array.isArray(field.state.value) ? field.state.value.length + 1 : 1

            return {
              title: `Row title ${nextRowIndex}`,
              description: '',
              id: v4(),
            }
          },
          addItemButton: ({ state, ...rest }) => {
            const totalRows = (state?.sections ?? []).reduce(
              (
                sum: number,
                section:
                  | { rows?: { title: string; id: string; description?: string }[] }
                  | undefined,
              ) => sum + (section?.rows?.length || 0),
              0,
            )
            return (
              <div className="col-span-full flex justify-start -my-4">
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  className="m-0 p-0 form-button"
                  {...rest}
                  disabled={totalRows >= 10}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add list item
                </Button>
              </div>
            )
          },
          removeItemButton: <X className="w-4 h-4 absolute text-tertiary top-0 right-0" />,
          subFields: [
            {
              name: 'sections[0].rows[0].title',
              label: 'Row title',
              componentType: 'input',
              className: 'w-full',
              zodValidation: z
                .string()
                .min(1, 'Row title is required')
                .max(24, 'Row title cannot exceed 24 characters'),
              inputProps: {
                placeholder: 'Enter row title',
              },
            },
            {
              name: 'sections[0].rows[0].description',
              label: 'Row description (optional)',
              componentType: 'input',
              className: 'w-full',
              zodValidation: z
                .string()
                .max(72, 'Row description cannot exceed 72 characters')
                .optional(),
              inputProps: {
                placeholder: 'Enter row description',
              },
            },
          ],
        },
      ],
    },
    {
      name: 'template',
      zodValidation: (values) =>
        values.whatsapp_msg_type === 'template'
          ? z.record(z.any()).refine((obj) => Object.keys(obj).length > 0, {
              message: 'Template is required when whatsapp message type is template',
            })
          : z.string().optional(),
      visible: (values) => values.whatsapp_msg_type === 'template' && values.from !== null,
      onChangeListenTo: ['whatsapp_msg_type', 'from', 'template'],
      component: ({ form }) => (
        <TemplateSelect
          showApprovedOnly
          selectedSender={form.state.values.from || undefined}
          form={form as unknown as GenericFormType<WhatsAppFormType>}
        />
      ),
      type: 'custom',
    },
    {
      name: 'variables',
      zodValidation: z.any(),
      className: 'overflow-hidden',
      visible: (values) =>
        values.whatsapp_msg_type === 'template' &&
        values.template &&
        Object.keys(values.template).length > 0,
      onChangeListenTo: ['whatsapp_msg_type', 'template', 'from'],
      component: ({ form }) => (
        <VariableEditor
          form={form as unknown as GenericFormType<WhatsAppFormType>}
          value={form.state.values.variables}
          nodeVars={nodeVars}
        />
      ),
      type: 'custom',
    },
  ]
}

const fields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericFieldConfig<AiAgentWhatsappNodeformData> => {
  const validations = v2validations({ nodeVars }).ai_agent_whatsapp
  const baseFields = aiAgentsFields(validations, FlowNodeTypes.ai_agent_whatsapp, nodeVars)

  const whatsappFields = whatsappSpecificFields(nodeVars, fieldsWithDynamicNodeVars)

  // Separate response-related fields from other base fields to control their position in the form

  const commonFields = baseFields.filter(
    (field) => field.name !== 'wait_for_response' && field.name !== 'reply_timeout',
  )
  const responseFields = baseFields.filter(
    (field) => field.name === 'wait_for_response' || field.name === 'reply_timeout',
  )

  return [
    ...commonFields,
    ...whatsappFields,
    ...responseFields,
  ] as GenericFieldConfig<AiAgentWhatsappNodeformData>
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const AiAgentWhatsappSidebarComponent = (
  props: SidebarComponentProps<AiAgentWhatsappNodeData>,
) => {
  const { nodeVars } = props
  const { data: templatesResponse } = useWhatsAppTemplatesQuery()
  const senderIds = templatesResponse?.map((template) => template.sender_ids)
  const fieldsWithDynamicNodeVars = {
    from: [...(senderIds?.flat() || []), ...nodeVars],
  }

  // Default from value is the Start.whatsapp.to if it exists, else the first sender id
  const defaultFrom = nodeVars.includes('{{Start.whatsapp.to}}')
    ? '{{Start.whatsapp.to}}'
    : fieldsWithDynamicNodeVars.from[0]

  // If `{{Start.whatsapp.from}}` exists in nodeVars, use it as default to value, else empty string
  const defaultTo = nodeVars.includes('{{Start.whatsapp.from}}') ? '{{Start.whatsapp.from}}' : ''
  const formFields = fields(nodeVars, fieldsWithDynamicNodeVars)

  const { selectedNode } = props

  return (
    <GenericSidebarComponent
      fields={formFields}
      {...props}
      className="relative"
      defaultValues={{
        from: selectedNode.data.config.from || defaultFrom,
        to: selectedNode.data.config.to || defaultTo,
      }}
    />
  )
}
